import {
  ManagedServiceId,
  getServiceNameFromServiceId,
} from '@axinom/mosaic-id-utils';

import {
  type FormActionData,
  createUpdateGQLFragmentGenerator,
  formatTitleCase,
} from '@axinom/mosaic-ui';
import gql from 'graphql-tag';
import {
  type ChangeManagedServiceStateMutationVariables,
  type ChangeServiceStatePayload,
  type EnvironmentManagedService,
  EnvironmentOrchestrationAndManagedServiceDataDocument,
  type EnvironmentOrchestrationAndManagedServiceDataQuery,
  type EnvironmentOrchestrationAndManagedServiceDataQueryVariables,
  EnvironmentServiceState,
  type Mutation,
  OrchestrationState,
  ServiceCommand,
} from '../generated/id-service-env-administration';
import errorDisable from '../images/status/disable_error.svg';
import disabled from '../images/status/disabled.svg';
import disabling from '../images/status/disabling.svg';
import errorEnable from '../images/status/enable_error.svg';
import enabled from '../images/status/enabled.svg';
import enabled_always from '../images/status/enabled_always.svg';
import enabling from '../images/status/enabling.svg';
import { apolloClient } from '../modules/apolloClient';

/**
 * Returns icon for the managed service state.
 */
export const getServiceStateIcon = (
  serviceState: EnvironmentServiceState | undefined,
  serviceId: ManagedServiceId,
): string => {
  let icon;

  if (serviceId === 'ax-micro-frontend-service' && serviceState === 'ENABLED') {
    icon = enabled_always;
  } else if (serviceId === 'ax-portal-service' && serviceState === 'ENABLED') {
    icon = enabled_always;
  } else if (serviceId === 'ax-hosting-service' && serviceState === 'ENABLED') {
    icon = enabled_always;
  } else if (serviceId === 'ax-id-service' && serviceState === 'ENABLED') {
    icon = enabled_always;
  } else if (serviceState === 'ENABLED') {
    icon = enabled;
  } else if (serviceState === 'DISABLED') {
    icon = disabled;
  } else if (serviceState === 'ENABLING') {
    icon = enabling;
  } else if (serviceState === 'DISABLING') {
    icon = disabling;
  } else if (serviceState === 'ERROR_ENABLING') {
    icon = errorEnable;
  } else if (serviceState === 'ERROR_DISABLING') {
    icon = errorDisable;
  } else {
    icon = '';
  }
  return icon;
};

export type ManagedServiceData = Pick<
  EnvironmentManagedService,
  | 'managedServiceId'
  | 'managedServiceName'
  | 'isCoreService'
  | 'sortOrder'
  | 'state'
  | 'environmentId'
  | 'tenantId'
  | 'updatedAt'
  | 'updatedBy'
>;

/**
 * Returns the details about the `managedServiceId` of the given `environmentId`
 */
export const getManagedServiceData = async (
  envId: string,
  managedServiceId: ManagedServiceId,
): Promise<ManagedServiceData | undefined> => {
  const client = apolloClient();

  const managedServiceData = await client.query<
    EnvironmentOrchestrationAndManagedServiceDataQuery,
    EnvironmentOrchestrationAndManagedServiceDataQueryVariables
  >({
    query: EnvironmentOrchestrationAndManagedServiceDataDocument,
    variables: {
      id: envId,
      condition: {
        environmentId: envId,
      },
      filter: {
        managedServiceId: {
          equalTo: managedServiceId,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const managedServiceResult =
    managedServiceData.data.environmentManagedServices?.nodes[0];

  return managedServiceResult;
};

/**
 * Formats the passed in snake_case ENUM values to `titleCase`.
 *
 * i.e.
 * ERROR_ENABLING -> 'Error Enabling'
 * IN_PROGRESS    -> 'In Progress'
 */
export const formatEnumValueToTitleCase = (
  enumValue: string | undefined,
): string => {
  return formatTitleCase((enumValue ?? '').split('_').join(' ')) ?? '';
};

/**
 * Mutates the passed in `ActionData` object. Adds either the `Disable` or `Enable` action for a managed-service depending on
 * the possibility of invoking the action based on the `currentServiceState` of that service on the given `environmentId`.
 *
 * Also accepts an optional `callback` that will be called after a specific action is invoked. The `callback` will contain
 * an argument of the `newServiceState` after invoking the action.
 */
export const addEnableDisableActionForService = (
  actions: FormActionData[],
  environmentId: string,
  serviceId: string,
  currentServiceState: EnvironmentServiceState | undefined,
  callback?: (newServiceState: EnvironmentServiceState) => void,
): void => {
  if (
    currentServiceState === EnvironmentServiceState.Enabled ||
    currentServiceState === EnvironmentServiceState.ErrorDisabling
  ) {
    actions.unshift({
      label: 'Disable',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: 'Disabling a service is a destructive operation. It will delete the current configuration and related data for the service. Please confirm to proceed.',
      },
      onActionSelected: async () => {
        const result = await changeServiceState(
          environmentId,
          ServiceCommand.Disable,
          serviceId,
        );

        if (callback !== undefined) {
          callback(result.newServiceState);
        }
      },
    });
  }

  if (
    currentServiceState === EnvironmentServiceState.Disabled ||
    currentServiceState === EnvironmentServiceState.ErrorEnabling
  ) {
    actions.unshift({
      label: 'Enable',
      onActionSelected: async () => {
        const result = await changeServiceState(
          environmentId,
          ServiceCommand.Enable,
          serviceId,
        );

        if (callback !== undefined) {
          callback(result.newServiceState);
        }
      },
    });
  }
};

const changeServiceState = async (
  environmentId: string,
  commandToSend: ServiceCommand,
  serviceId: string,
): Promise<{
  newServiceState: EnvironmentServiceState;
}> => {
  const client = apolloClient();

  const mutation = `change${0}:${generateChangeServiceStateMutation(
    environmentId,
    commandToSend,
    serviceId,
  )}`;

  const GqlDoc = gql`
    mutation changeManagedServiceState {
      ${mutation}
    }
  `;

  await client.mutate({
    mutation: GqlDoc,
    fetchPolicy: 'no-cache',
  });

  return {
    newServiceState:
      commandToSend === ServiceCommand.Enable
        ? EnvironmentServiceState.Enabling
        : EnvironmentServiceState.Disabling,
  };
};

const generateChangeServiceStateMutation = (
  environmentId: string,
  commandToSend: ServiceCommand,
  managedServiceId: string,
): string => {
  const generateUpdateGQLFragment =
    createUpdateGQLFragmentGenerator<Mutation>();

  return generateUpdateGQLFragment<
    ChangeManagedServiceStateMutationVariables,
    ChangeServiceStatePayload
  >(
    'changeManagedServiceState',
    {
      input: {
        environmentId: environmentId,
        commandToSend: {
          type: 'enum',
          value: commandToSend,
        },
        managedServiceId,
      },
    },
    ['successfullySent'],
  );
};

/**
 * Checks the status of a managed service by using the orchestration state of the environment and the state of the managed service.
 * Returns a descriptive message if the managed service cannot be used.
 * This can happen if the orchestration of the environment is not complete, or the managed service is not enabled for the environment.
 * This message can be shown in a station as a `stationMessage`.
 * This function returns `null` if the managed service is available for use.
 *
 * TODO: We need to improve the return type of this method. Maybe return a complex type (i.e. {code, message}).
 * Then the consumers can have logic based on the the well-known codes.
 */
export const getManagedServiceAvailabilityMessage = async (
  managedServiceId: ManagedServiceId,
  environmentId: string,
): Promise<string | null> => {
  const client = apolloClient();
  const result = await client.query<
    EnvironmentOrchestrationAndManagedServiceDataQuery,
    EnvironmentOrchestrationAndManagedServiceDataQueryVariables
  >({
    query: EnvironmentOrchestrationAndManagedServiceDataDocument,
    variables: {
      id: environmentId,
      condition: {
        environmentId,
      },
      filter: {
        managedServiceId: {
          equalTo: managedServiceId,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const orchestrationState =
    result.data.orchestrationResult?.orchestrationState;

  let message: string | null = null;
  if (orchestrationState === OrchestrationState.Pending) {
    message =
      'WARNING: Environment initialization is still pending. Please check when completed.';
  } else if (orchestrationState === OrchestrationState.InProgress) {
    message =
      'WARNING: Environment initialization is still in progress. Please check when completed.';
  } else if (orchestrationState === OrchestrationState.Error) {
    message =
      'WARNING: Environment initialization has failed. Please check the Environment Details station for more details.';
  } else if (orchestrationState === OrchestrationState.Completed) {
    const serviceData = result.data?.environmentManagedServices?.nodes.find(
      (service) => service.managedServiceId === managedServiceId,
    );
    if (serviceData?.state !== EnvironmentServiceState.Enabled) {
      const serviceName = getServiceNameFromServiceId(managedServiceId, true);
      if (serviceData?.isCoreService === true) {
        message = `WARNING: ${serviceName} is a core-service and has not been enabled. Please contact Axinom Support.`;
      } else {
        message = `WARNING: ${serviceName} is not enabled. Please enable the service from the Environment Details.`;
      }
    }
  }
  return message;
};
